import React, { useCallback, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { IconLink, Link, Logo, MiniBasket, QuickSearch } from '@/components/shared';
import { TopBar } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, LayoutResponse, PageType, TranslationsWebsite } from '@/generated/data.schemas';
import { useGetSettings } from '@/generated/settings';
import { formatTranslation } from '@/helpers';
import { DrawerMenu } from './DrawerMenu';
import styles from './Header.module.scss';

interface HeaderProps {
	critical?: boolean;
	disableQuickBasket?: boolean;
	hideCustomerSelector?: boolean;
	layout: LayoutResponse | undefined;
	translations: TranslationsWebsite | undefined;
	isSticky?: boolean;
}

// TODO:
// - Add support for focus trapping on DrawerMenu

const Header: React.FunctionComponent<HeaderProps> = ({
	critical,
	disableQuickBasket,
	hideCustomerSelector,
	layout,
	translations,
	isSticky,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: settings } = useGetSettings(segmentationId);
	const user = settings?.data.user;
	const staticLinks = layout?.staticLinks;

	const frontPageUrl = layout?.frontPageUrl;

	const isSalesPortal = settings?.data.isSalesPortal ?? true;

	const [scrollPos, setScrollPos] = React.useState(0);
	const [classList, setClassList] = React.useState<string[]>([]);
	const headerRef = useRef<HTMLElement>(null);

	const modifyClassList = useCallback((toBeAdded: string[], toBeRemoved: string[]) => {
		setClassList((prev) => {
			const next = prev.filter((cls) => !toBeRemoved.includes(cls));
			toBeAdded.forEach((cls) => {
				if (!next.includes(cls)) next.push(cls);
			});
			return next;
		});
	}, []);

	const onScroll = useCallback(() => {
		const position = window.scrollY;
		const thresholdUp = scrollPos - 50;
		const thresholdDown = scrollPos + 150;

		if (position === 0) {
			modifyClassList([styles.absolute], [styles.push, styles.show]);
		} else if (position > 200 && position > thresholdDown && !classList.includes(styles.push)) {
			modifyClassList([styles.push], [styles.show, styles.absolute]);
		} else if (position < thresholdUp && classList.includes(styles.push)) {
			modifyClassList([styles.show], [styles.push, styles.absolute]);
		}

		if (position > thresholdDown || position < thresholdUp) {
			setScrollPos(position);
		}
	}, [scrollPos, classList, modifyClassList]);

	useEffect(() => {
		if (!isSticky) {
			return;
		}
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [onScroll, isSticky]);

	return (
		<header ref={headerRef} className={classNames(styles.wrapper, ...classList)}>
			{user?.isImpersonating && (
				<div className={styles.impersonateBar}>
					{formatTranslation(translations?.auth.impersonating || '', { 0: user.userName })}
				</div>
			)}

			<TopBar hideCustomerSelector={hideCustomerSelector || !isSalesPortal} />

			<section className={classNames(styles.header, { [styles.isCritical]: critical }, 'u-container-lg')}>
				<DrawerMenu triggerButtonClassName={styles.drawerMenu} />

				<Link
					className={styles.logo}
					to={frontPageUrl || '/'}
					color={'default'}
					title={translations?.shared.siteName}
					hasMinHeight
				>
					<Logo className={styles.logoAsset} name={'ccj'} />
				</Link>
				{isSalesPortal && (
					<>
						<QuickSearch className={styles.search} />

						<nav className={styles.userMenu} aria-label={translations?.shared.navigation.user}>
							<IconLink
								to={staticLinks?.find((item) => item.pageType === PageType.CustomersList)?.url}
								state={{ area: Area.StaticPages, pageType: PageType.CustomersList }}
								icon={'locator'}
								label={translations?.customers.headerLinkLabel || ''}
								hideLabelOnMobile
								noRadius
							/>
							<MiniBasket disableQuickBasket={disableQuickBasket} />
						</nav>
					</>
				)}
			</section>
		</header>
	);
};

export const MemoizedHeader = React.memo(Header);
